
import { Card } from "@/components/ui/card";
import { Trip } from "@/types/trip";
import TripCard from "../TripCard";
import { useTranslation } from "react-i18next";
import { useProcessedTrips } from "@/hooks/useProcessedTrips";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseISO, compareDesc } from "date-fns";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface DesktopTripListProps {
  futureTrips: Trip[];
  pastTrips: Trip[];
  handleRefreshTrips?: () => void;
}

export const DesktopTripList = ({ futureTrips, pastTrips, handleRefreshTrips }: DesktopTripListProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  // Use our processed trips hook to ensure consistent data processing
  const { processedFutureTrips, processedPastTrips, isProcessing } = useProcessedTrips(futureTrips, pastTrips);
  
  console.debug("======== DESKTOP TRIP LIST ==========");
  console.debug("Future trips count:", futureTrips.length);
  console.debug("Processed future trips count:", processedFutureTrips.length);
  
  if (processedFutureTrips.length > 0) {
    console.debug("First future trip destinationImage:", processedFutureTrips[0].destinationImage);
  }

  // Sort future trips from most future at the top (latest start date first)
  const sortedFutureTrips = [...processedFutureTrips].sort((a, b) => {
    // Get the first element's start date or fallback to sorting by remainingDays
    const aStartDate = a.elements?.length > 0 ? parseISO(a.elements[0].startDate || "1970") : null;
    const bStartDate = b.elements?.length > 0 ? parseISO(b.elements[0].startDate || "1970") : null;
    
    if (aStartDate && bStartDate) {
      return compareDesc(aStartDate, bStartDate); // Most future dates first
    }
    
    // Fallback to remainingDays if no elements or dates
    return (b.remainingDays || 0) - (a.remainingDays || 0);
  });
  
  // Sort past trips from most recent at the top (latest end date first)
  const sortedPastTrips = [...processedPastTrips].sort((a, b) => {
    // Get the last element's end date
    const aEndDate = a.elements?.length > 0 ? parseISO(a.elements[a.elements.length - 1].endDate || "1970") : null;
    const bEndDate = b.elements?.length > 0 ? parseISO(b.elements[b.elements.length - 1].endDate || "1970") : null;
    
    if (aEndDate && bEndDate) {
      return compareDesc(aEndDate, bEndDate); // Most recent past trips first
    }
    
    return 0; // Keep original order if no dates available
  });

  const handleTripCardClick = (trip: Trip) => {
    console.debug("🔥 Trip card clicked in desktop mode:", trip.id);
    console.debug("🔥 Is mobile?", isMobile);
    
    if (!isMobile) {
      console.debug("🔥 Navigating to trip timeline");
      navigate('/trip-timeline', { 
        state: { 
          trip
          // Removed the function that can't be cloned
        }
      });
    }
  };

  if (isProcessing) {
    return (
      <div className="px-4 py-6 max-w-5xl mx-auto">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-travel-500" />
        </div>
      </div>
    );
  }
  
  // Combine future and past trips to display in order from most future to most past
  const allTripsInOrder = [...sortedFutureTrips, ...sortedPastTrips];

  return (
    <div className="px-4 py-6 max-w-5xl mx-auto">
      {allTripsInOrder.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {allTripsInOrder.map((trip, index) => (
            <motion.div 
              key={trip.id} 
              className="p-2 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => {
                console.debug("🔥 Trip card container clicked:", trip.id);
                handleTripCardClick(trip);
              }}
            >
              <TripCard trip={trip} handleRefreshTrips={handleRefreshTrips} />
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">{t("index.noTrips", "No trips found")}</p>
        </div>
      )}
    </div>
  );
}
